import { useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useNotifications } from '../contexts/NotificationContext';
import { TYPE_ICONS, DEFAULT_ICON } from '../constants/notificationTypes';

export default function NotificationToast() {
  const { newNotification, clearToast } = useNotifications();
  const navigate = useNavigate();

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (!newNotification) return;

    const timer = setTimeout(() => {
      clearToast();
    }, 5000);

    return () => clearTimeout(timer);
  }, [newNotification, clearToast]);

  const handleClick = () => {
    if (newNotification?.data?.link) {
      navigate(newNotification.data.link);
    }
    clearToast();
  };

  const Icon = newNotification
    ? TYPE_ICONS[newNotification.type] || DEFAULT_ICON
    : DEFAULT_ICON;

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-50 flex items-start justify-end px-4 py-20 sm:p-6 sm:pt-20"
    >
      <Transition show={!!newNotification}>
        <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border border-skin bg-surface-elevated shadow-dropdown transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0 data-leave:duration-100 data-leave:ease-in">
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40">
                <Icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
              </div>
              <div
                className="ml-3 w-0 flex-1 cursor-pointer pt-0.5"
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClick();
                }}
              >
                <p className="text-sm font-semibold text-skin-primary">
                  {newNotification?.title}
                </p>
                {newNotification?.message && (
                  <p className="mt-1 line-clamp-2 text-sm text-skin-secondary">
                    {newNotification.message}
                  </p>
                )}
              </div>
              <div className="ml-4 flex shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearToast();
                  }}
                  className="inline-flex rounded-md text-skin-tertiary hover:text-skin-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Fermer</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}
